<template>
  <div
    class="item-card file-card"
    :class="{ disabled: isFileSelectionDisabled }"
    @click="selectFile"
  >
    <div class="item-card__image">
      <img v-lazy="fileBanner" alt="" v-if="fileBanner" />
      <icon-base
        v-else
        class="file-card-icon"
        :icon="fileIcon"
        :font-icon="portal"
      />

      <div class="file-card__status" v-if="isFileSelectionDisabled">
        {{ fileState || 'Processing...' }}
      </div>

      <IngestedBadge
        v-if="!file.default && !gallery && !portal"
        type="file"
        :id="file.id"
      />
    </div>

    <content-line
      :icon="fileIcon"
      :font-icon="portal"
      :text="file.filename"
      tooltip
    >
      <template #actions v-if="!file.default">
        <template v-if="gallery">
          <v-btn
            v-if="isScorm"
            fab
            icon
            small
            @click.stop="$emit('uploadFile')"
          >
            <v-icon color="appMain">cloud_upload</v-icon>
          </v-btn>

          <v-btn
            v-if="isFileSelectionDisabled"
            fab
            icon
            small
            @click.stop="refreshFile()"
          >
            <v-icon color="appMain">refresh</v-icon>
          </v-btn>
        </template>

        <div @click.stop>
          <file-controls
            :file="file"
            :gallery="gallery"
            :viewOnly="!gallery && viewOnly"
            :deleteOnly="!gallery && deleteOnly"
          />
        </div>
      </template>
    </content-line>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import EventBus from '@/factories/eventBus';

import FileControls from '@/views/files/files/FileControls.vue';
import IngestedBadge from '@/components/basic/IngestedBadge.vue';

export default {
  name: 'FileCard',
  components: {
    FileControls,
    IngestedBadge,
  },
  props: {
    file: {
      type: Object,
      default: () => ({}),
    },
    assetOnly: Boolean,
    viewOnly: Boolean,
    gallery: Boolean,
    portal: Boolean,
    deleteOnly: Boolean,
  },
  computed: {
    fileIcon() {
      return this.getFileTypeIcon(this.file.mimetype);
    },
    fileBanner() {
      return this.getFileBanner(this.file);
    },
    isScorm() {
      return this.file.type === 'scorm';
    },
    fileState() {
      return this.file?.asset?.state;
    },
    isFileSelectionDisabled() {
      if (!this.assetOnly) return false;
      return this.fileState !== 'ready';
    },
  },
  methods: {
    ...mapActions('files', ['loadOne']),

    getFileBanner(file) {
      if (this.isFileSelectionDisabled || !file) return null;

      const { mimetype = '', asset = {}, url = '' } = file;

      const handleFrom = (u) => (u.split('/').pop() || '').split('?')[0];
      const isFilestack = url.includes('filestackcontent.com');
      const resizeParams = 'resize=width:640';

      // image
      if (mimetype.includes('image')) {
        return !this.disableResize && url
          ? this.minifyFilestackImage(url, mimetype)
          : url || null;
      }

      // pdf
      if (mimetype === 'application/pdf' && isFilestack) {
        const handle = handleFrom(url);
        return `https://cdn.filestackcontent.com/output=format:jpg,density:150,page:1/${resizeParams}/${handle}`;
      }

      if (mimetype.includes('video')) {
        // Mux
        const muxId = asset.playback_ids?.[0]?.id;
        if (muxId) {
          return `https://image.mux.com/${muxId}/thumbnail.png`;
        }

        // SproutVideo
        if (asset.thumb_big) {
          return asset.thumb_big;
        }

        // Filestack (fallback)
        if (isFilestack) {
          return `https://cdn.filestackcontent.com/video_convert=preset:thumbnail/${handleFrom(
            url
          )}`;
        }
      }

      // Default
      return null;
    },

    getFileTypeIcon(mime) {
      const icons = {
        video: 'play_circle',
        audio: 'music_note',
        image: 'image',
        file: 'description',
      };

      let type = 'file';

      if (typeof mime === 'string' && mime.includes('/')) {
        const [mainType] = mime.split('/');
        if (icons[mainType]) type = mainType;
      }

      return this.portal ? icons[type] : `custom/${type}`;
    },

    refreshFile() {
      if (this.isFileSelectionDisabled && this.assetOnly) {
        this.loadOne({ path: this.file.id });
      }
    },

    selectFile() {
      if (!this.gallery) {
        EventBus.$emit('openFileViewer', this.file);
        return;
      }

      if (this.isFileSelectionDisabled) return;

      this.$emit('selectFile');
    },
  },
  mounted() {
    this.refreshFile();
  },
};
</script>

<style>
.file-card__status {
  position: absolute;
  top: 0;
  right: 0;
  padding: 4px 8px;
  color: var(--v-light-base);
  background: var(--v-warning-base);
}

.file-card {
  .item-card__image {
    background: var(--v-darkGray-base);
    display: flex;
    align-items: center;
    justify-content: center;

    .file-card-icon {
      height: 70%;
      top: 15%;

      &.v-icon {
        display: block;
        margin: auto;
        height: auto;
        width: auto;
        position: relative;
        font-size: 60px;
        color: var(--v-primary-base) !important;
      }
    }
  }

  .ingested-badge {
    position: absolute;
    top: 8px;
    left: 8px;
  }
}
</style>
